/**
 * Text Formatting Utilities for AI-Generated Narratives
 * Provides semantic highlighting, structure extraction, and readability improvements
 */

// Types for formatted content
export interface FormattedSegment {
  type: 'text' | 'number' | 'percentage' | 'time' | 'asset' | 'status' | 'action' | 'metric'
  content: string
  value?: number
  status?: 'good' | 'warning' | 'critical' | 'neutral'
}

export interface ExtractedMetric {
  label: string
  value: string | number
  unit?: string
  status?: 'good' | 'warning' | 'critical'
}

export interface StructuredNarrative {
  headline: string
  summary: string
  keyPoints: string[]
  metrics: ExtractedMetric[]
  action?: string
}

// Regex patterns for content detection
const PATTERNS = {
  percentage: /(\d+(?:\.\d+)?)\s*%/g,
  number: /\b(\d{1,3}(?:,\d{3})*(?:\.\d+)?)\b/g,
  time: /\b(\d+)\s*(hours?|hrs?|minutes?|mins?|days?|weeks?|months?)\b/gi,
  asset: /\b([A-Z]\d{5})\b/g,
  critical: /\b(critical|failure|failed|emergency|urgent|immediate)\b/gi,
  warning: /\b(warning|caution|attention|concern|degraded|declining)\b/gi,
  good: /\b(healthy|normal|optimal|improved|stable|success)\b/gi,
  action: /\b(schedule|inspect|replace|check|verify|review|monitor|investigate)\b/gi,
  metric: /\b(temperature|pressure|vibration|flow|rpm|efficiency|health\s*score)\b/gi
}

/**
 * Parse narrative text into formatted segments for rich rendering
 */
export function parseNarrative(text: string): FormattedSegment[] {
  if (!text) return []

  const segments: FormattedSegment[] = []
  let lastIndex = 0

  // Combined regex for all patterns
  const combinedPattern = new RegExp(
    `(${PATTERNS.percentage.source})|` +
    `(${PATTERNS.time.source})|` +
    `(${PATTERNS.asset.source})|` +
    `(${PATTERNS.critical.source})|` +
    `(${PATTERNS.warning.source})|` +
    `(${PATTERNS.good.source})|` +
    `(${PATTERNS.action.source})`,
    'gi'
  )

  let match: RegExpExecArray | null
  const allMatches: Array<{ index: number; length: number; type: FormattedSegment['type']; content: string; status?: FormattedSegment['status'] }> = []

  // Find all matches
  while ((match = combinedPattern.exec(text)) !== null) {
    let type: FormattedSegment['type'] = 'text'
    let status: FormattedSegment['status'] | undefined

    const content = match[0]

    if (PATTERNS.percentage.test(content)) {
      type = 'percentage'
      PATTERNS.percentage.lastIndex = 0
    } else if (PATTERNS.time.test(content)) {
      type = 'time'
      PATTERNS.time.lastIndex = 0
    } else if (PATTERNS.asset.test(content)) {
      type = 'asset'
      PATTERNS.asset.lastIndex = 0
    } else if (PATTERNS.critical.test(content)) {
      type = 'status'
      status = 'critical'
      PATTERNS.critical.lastIndex = 0
    } else if (PATTERNS.warning.test(content)) {
      type = 'status'
      status = 'warning'
      PATTERNS.warning.lastIndex = 0
    } else if (PATTERNS.good.test(content)) {
      type = 'status'
      status = 'good'
      PATTERNS.good.lastIndex = 0
    } else if (PATTERNS.action.test(content)) {
      type = 'action'
      PATTERNS.action.lastIndex = 0
    }

    allMatches.push({
      index: match.index,
      length: content.length,
      type,
      content,
      status
    })
  }

  // Sort matches by index
  allMatches.sort((a, b) => a.index - b.index)

  // Build segments
  for (const m of allMatches) {
    // Add plain text before this match
    if (m.index > lastIndex) {
      const plainText = text.slice(lastIndex, m.index)
      if (plainText) {
        segments.push({ type: 'text', content: plainText })
      }
    }

    // Add the matched segment
    segments.push({
      type: m.type,
      content: m.content,
      status: m.status
    })

    lastIndex = m.index + m.length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: text }]
}

/**
 * Extract metrics from narrative text
 */
export function extractMetrics(text: string): ExtractedMetric[] {
  const metrics: ExtractedMetric[] = []

  // Pattern: "label: value unit" or "label of value unit"
  const metricPattern = /(\w+(?:\s+\w+)?)\s*(?::|of|is|at)\s*(\d+(?:\.\d+)?)\s*(%|hours?|hrs?|psi|°[FC]|rpm)?/gi

  let match
  while ((match = metricPattern.exec(text)) !== null) {
    const label = match[1].trim()
    const value = parseFloat(match[2])
    const unit = match[3] || ''

    // Determine status based on context
    let status: ExtractedMetric['status'] = undefined
    const context = text.slice(Math.max(0, match.index - 50), match.index + match[0].length + 50).toLowerCase()

    if (context.includes('critical') || context.includes('fail') || context.includes('below')) {
      status = 'critical'
    } else if (context.includes('warning') || context.includes('elevated') || context.includes('concern')) {
      status = 'warning'
    } else if (context.includes('healthy') || context.includes('normal') || context.includes('optimal')) {
      status = 'good'
    }

    metrics.push({ label, value, unit, status })
  }

  return metrics
}

/**
 * Extract bullet points from narrative (sentences starting with common patterns)
 */
export function extractKeyPoints(text: string, maxPoints: number = 5): string[] {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 10)
  const keyPoints: string[] = []

  // Prioritize sentences with action words or metrics
  const priorityPatterns = [
    /recommend|suggest|should|must|need to|action|schedule/i,
    /critical|warning|alert|attention/i,
    /\d+%|\d+ hours?|\d+ days?/,
    /improve|decline|increase|decrease|trend/i
  ]

  for (const sentence of sentences) {
    const trimmed = sentence.trim()
    if (trimmed.length < 20) continue

    const priority = priorityPatterns.reduce((score, pattern) =>
      pattern.test(trimmed) ? score + 1 : score, 0)

    if (priority > 0 && keyPoints.length < maxPoints) {
      keyPoints.push(trimmed)
    }
  }

  // If we don't have enough, add first few sentences
  if (keyPoints.length < 2) {
    for (const sentence of sentences.slice(0, maxPoints - keyPoints.length)) {
      const trimmed = sentence.trim()
      if (trimmed.length >= 20 && !keyPoints.includes(trimmed)) {
        keyPoints.push(trimmed)
      }
    }
  }

  return keyPoints.slice(0, maxPoints)
}

/**
 * Generate a structured narrative from raw text
 */
export function structureNarrative(text: string): StructuredNarrative {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 5)

  // First sentence is typically the headline/summary
  const headline = sentences[0]?.trim() || ''

  // Look for summary patterns
  let summary = ''
  for (const sentence of sentences.slice(1, 4)) {
    const trimmed = sentence.trim()
    if (trimmed.length > 30 && !summary) {
      summary = trimmed
    }
  }
  if (!summary) summary = headline

  // Extract structured data
  const keyPoints = extractKeyPoints(text, 4)
  const metrics = extractMetrics(text)

  // Find action recommendation
  let action: string | undefined
  for (const sentence of sentences) {
    if (/recommend|suggest|should|action|schedule|inspect/i.test(sentence)) {
      action = sentence.trim()
      break
    }
  }

  return {
    headline,
    summary,
    keyPoints,
    metrics,
    action
  }
}

/**
 * Truncate text intelligently at sentence boundaries
 */
export function truncateText(text: string, maxLength: number = 150): string {
  if (text.length <= maxLength) return text

  // Try to cut at sentence boundary
  const sentences = text.slice(0, maxLength + 50).split(/[.!?]+/)
  let result = ''

  for (const sentence of sentences) {
    const potential = result + sentence.trim() + '. '
    if (potential.length <= maxLength) {
      result = potential
    } else {
      break
    }
  }

  if (!result) {
    // Fall back to word boundary
    result = text.slice(0, maxLength).replace(/\s+\S*$/, '') + '...'
  }

  return result.trim()
}

/**
 * Calculate reading time for narrative
 */
export function estimateReadingTime(text: string): string {
  const words = text.split(/\s+/).length
  const minutes = Math.ceil(words / 200) // Average reading speed
  return minutes <= 1 ? '< 1 min read' : `${minutes} min read`
}

/**
 * Get severity from narrative content
 */
export function detectSeverity(text: string): 'critical' | 'warning' | 'info' | 'success' {
  const lowerText = text.toLowerCase()

  if (/critical|emergency|immediate|failure|failed|urgent/.test(lowerText)) {
    return 'critical'
  }
  if (/warning|caution|attention|concern|elevated|degraded/.test(lowerText)) {
    return 'warning'
  }
  if (/success|healthy|optimal|normal|improved|stable/.test(lowerText)) {
    return 'success'
  }
  return 'info'
}

/**
 * Format a number with appropriate precision and locale for display
 */
export function formatDisplayNumber(value: number, options?: {
  decimals?: number
  compact?: boolean
  prefix?: string
  suffix?: string
}): string {
  const { decimals = 1, compact = false, prefix = '', suffix = '' } = options || {}

  let formatted: string
  if (compact && Math.abs(value) >= 1000) {
    const suffixes = ['', 'K', 'M', 'B']
    const tier = Math.floor(Math.log10(Math.abs(value)) / 3)
    const scaledValue = value / Math.pow(1000, tier)
    formatted = scaledValue.toFixed(decimals) + suffixes[tier]
  } else {
    formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    })
  }

  return `${prefix}${formatted}${suffix}`
}

/**
 * Generate plain English status description
 */
export function getStatusDescription(
  value: number,
  thresholds: { warning: number; critical: number },
  metric: string,
  higherIsBetter: boolean = true
): string {
  const isCritical = higherIsBetter ? value < thresholds.critical : value > thresholds.critical
  const isWarning = higherIsBetter ? value < thresholds.warning : value > thresholds.warning

  if (isCritical) {
    return `${metric} is critically ${higherIsBetter ? 'low' : 'high'} and requires immediate attention.`
  }
  if (isWarning) {
    return `${metric} is ${higherIsBetter ? 'below' : 'above'} optimal levels. Monitor closely.`
  }
  return `${metric} is within healthy operating range.`
}
